import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBox } from '../context/BoxContext';
import { productService, promotionService } from '../services/firebaseService';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToBox } = useBox();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isPromotion, setIsPromotion] = useState(false);

  useEffect(() => {
    loadProduct();
    // eslint-disable-next-line
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      
      // Check if we're on a promotion route
      const currentPath = window.location.pathname;
      const isPromotionRoute = currentPath.includes('/promotions/');
      setIsPromotion(isPromotionRoute);
      
      let data;
      if (isPromotionRoute) {
        data = await promotionService.getPromotionById(id);
      } else {
        data = await productService.getProductById(id);
      }
      
      setProduct(data);
      setError(null);
    } catch (error) {
      console.error('❌ Erreur lors du chargement du produit :', error);
      setError(error.message || 'Erreur inconnue');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD'
    }).format(price);
  };

  const handleAddToBox = () => {
    if (product.stockQuantity <= 0) {
      toast.error('Désolé, ce produit est actuellement en rupture de stock');
      return;
    }

    addToBox(product, quantity);
    setQuantity(1); // Réinitialiser la quantité après l'ajout
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p className="loading-message">Chargement des détails du produit...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="error">
            <h2>Produit introuvable</h2>
            <p>Le produit avec l'ID {id} n'a pas pu être chargé.</p>
            <p><strong>Erreur :</strong> {error}</p>
            <div style={{ marginTop: '20px' }}>
              <button onClick={() => navigate('/')} className="btn btn-primary" style={{ marginRight: '10px' }}>
                <i className="fas fa-home"></i>
                Accueil
              </button>
              <button onClick={() => navigate('/products')} className="btn btn-outline">
                <i className="fas fa-pills"></i>
                Tous les produits
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="error">
            <h2>Produit introuvable</h2>
            <p>Le produit avec l'ID {id} n'existe pas.</p>
            <button onClick={() => navigate('/products')} className="btn btn-primary">
              <i className="fas fa-arrow-left"></i>
              Retour aux produits
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <button 
            onClick={() => navigate('/')} 
            className="breadcrumb-link"
          >
            <i className="fas fa-home"></i>
            Accueil
          </button>
          <span className="breadcrumb-separator">
            <i className="fas fa-chevron-right"></i>
          </span>
          <button 
            onClick={() => navigate(isPromotion ? '/promotions' : '/products')} 
            className="breadcrumb-link"
          >
            {isPromotion ? 'Promotions' : 'Produits'}
          </button>
          <span className="breadcrumb-separator">
            <i className="fas fa-chevron-right"></i>
          </span>
          <span className="breadcrumb-current">{product.name}</span>
        </nav>

        {/* Product Detail */}
        <div className="product-detail">
          <div className="product-image-section">
            <div className="product-image-container">
              <img 
                src={product.imageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop'} 
                alt={product.name}
                className="product-image"
              />
              {/* PROMO Badge for promotional products */}
              {isPromotion && (
                <div className="promo-badge">
                  <span>PROMO</span>
                </div>
              )}
            </div>
          </div>

          <div className="product-info-section">
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              {/* Show all categories if present */}
              <p className="product-subtitle">
                {Array.isArray(product.categories)
                  ? product.categories.join(', ')
                  : (product.category || '')}
              </p>
              
              <div className="product-price-container">
                <p className="product-price">
                    <span className="current-price">{formatPrice(product.price)}</span>
                        {' '}
                        <span className="old-price">
                        {formatPrice(product.price + 20)}
                        </span>
                </p>
                <div className="product-stock">
                  <span className={`stock-badge ${product.stockQuantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                    <i className={`fas ${product.stockQuantity > 0 ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                    {product.stockQuantity > 0 ? `En stock` : 'Rupture de stock'}
                  </span>
                </div>
              </div>
            </div>

            <div className="product-description">
              <h3>
                <i className="fas fa-info-circle"></i>
                Description
              </h3>
              <p>{product.description}</p>
            </div>

            {product.ingredients && (
              <div className="product-ingredients">
                <h3>
                  <i className="fas fa-list"></i>
                  Ingrédients clés
                </h3>
                <p>{product.ingredients}</p>
              </div>
            )}

            <div className="product-features">
              <h3>
                <i className="fas fa-star"></i>
                Bénéfices clés
              </h3>
              <ul className="features-list">
                <li>
                  <i className="fas fa-check"></i>
                  Ingrédients de qualité premium
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  Testé en laboratoire pour la pureté
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  Livraison rapide et fiable
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  Conseils professionnels disponibles
                </li>
              </ul>
            </div>

            <div className="product-actions">
              {/* Quantity Selector */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem', 
                marginBottom: '1.5rem',
                padding: '1rem',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{ fontWeight: '500', color: '#374151' }}>
                  <i className="fas fa-sort-numeric-up"></i>
                  Quantité :
                </label>
                <select
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  disabled={product.stockQuantity <= 0}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    minWidth: '80px'
                  }}
                >
                  {Array.from({ length: Math.min(10, product.stockQuantity) }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span style={{ color: '#023A1B', fontSize: '0.875rem' }}>
                  (Max : {Math.min(10, product.stockQuantity)})
                </span>
              </div>

              <button 
                className="btn btn-primary btn-large btn-order"
                onClick={handleAddToBox}
                disabled={product.stockQuantity <= 0}
                style={{ width: '100%', marginBottom: '1rem' }}
              >
                <i className="fas fa-box"></i>
                {product.stockQuantity > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
              </button>
              
              <div className="action-info">
                <div className="info-item">
                  <i className="fas fa-shield-alt"></i>
                  Produits 100% authentiques
                </div>
                <div className="info-item">
                  <i className="fas fa-shipping-fast"></i>
                  Livraison en 1 à 2 jours
                </div>
                <div className="info-item">
                  <i className="fas fa-credit-card"></i>
                  Paiement à la livraison
                </div>
                <div className="info-item">
                  <i className="fas fa-phone"></i>
                  Confirmation de la commande par téléphone
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="additional-info">
          <div className="info-section">
            <h3>
              <i className="fas fa-truck"></i>
              Détails livraison
            </h3>
            <p>
              🚚 Livraison rapide et sûre partout au Maroc !
            </p>
            <p>
              Expédition express en 24h-48h après validation de votre commande.👭 Astuce groupe : Nous vous aidons à partager les frais de livraison entre copines ou en famille - moins de 10 DH par personne ! 🎁
            </p>
          </div>

          <div className="info-section">
            <h3>
              <i className="fas fa-undo"></i>
              Politique de retour
            </h3>
              <p>
                Si votre commande pose problème, contactez-nous. Nous vous indiquerons la marche à suivre.
              </p>
          </div>

          <div className="info-section">
            <h3>
              <i className="fas fa-certificate"></i>
              Assurance qualité
            </h3>
            <p>
              Nous travaillons exclusivement avec les laboratoires officiels et leurs distributeurs agréés.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;